'use client';

import { useEffect, useState } from 'react';
import { getNetworkNameById } from '@/utils/networkNames';
import {
  VerificationPage,
  type VerificationData,
  type VerificationStatus,
} from '@/components/public/verification-page';
import StyledQRCode from '@/components/StyledQRCode';
import { generateMarginTexts } from '@/utils/qrHelpers';
import type { VerificationTemplateProps } from './types';

function resolveLogoSrc(raw: string | null | undefined) {
  const value = (raw ?? '').trim();
  if (!value) return undefined;
  if (/^(https?:\/\/|data:)/.test(value)) return value;
  if (value.startsWith('/')) {
    const apiBase = (process.env.NEXT_PUBLIC_API_BASE || '').replace(/\/+$/, '');
    return apiBase ? `${apiBase}${value}` : value;
  }
  return value;
}

const isHex = (v: string) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v.trim());
const withFallback = (v: string | null | undefined, fallback: string) =>
  (v ?? '').trim() && isHex((v ?? '').trim()) ? (v as string).trim() : fallback;

function initialsOf(name: string) {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (!w.length) return '?';
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
}

const STATUS_MAP: Record<string, VerificationStatus> = {
  valid: 'VALID',
  expired: 'EXPIRED',
  revoked: 'REVOKED',
};

/** "Valid until 31 December 2030" / "Expired 2 years ago · 9 February 2024" */
function statusPhrase(status: VerificationStatus, expiryLabel: string | null, expiryIso: string) {
  if (status === 'REVOKED') return 'This certificate was revoked by the issuer.';
  if (!expiryLabel) return status === 'VALID' ? 'Valid — no expiry date set.' : 'Expired.';
  if (status === 'VALID') return `Valid until ${expiryLabel}`;
  const then = new Date(expiryIso).getTime();
  if (Number.isNaN(then)) return `Expired ${expiryLabel}`;
  const years = Math.floor((Date.now() - then) / (365 * 86_400_000));
  const months = Math.floor((Date.now() - then) / (30 * 86_400_000));
  const ago =
    years >= 1
      ? `${years} year${years === 1 ? '' : 's'} ago`
      : months >= 1
        ? `${months} month${months === 1 ? '' : 's'} ago`
        : 'recently';
  return `Expired ${ago} · ${expiryLabel}`;
}

/**
 * Sixth verification template. Opt-in from Branding; no existing tenant is moved
 * onto it. Adapts the shared VerificationTemplateProps to the design component.
 */
export default function TemplateTrustPanel({
  certificate,
  tenant,
  verificationCode,
  qrUrl,
}: VerificationTemplateProps) {
  // Dates are formatted after mount so the server and client markup agree.
  const [issued, setIssued] = useState('');
  const [expiry, setExpiry] = useState<string | null>(null);

  useEffect(() => {
    const f = (iso: string) =>
      iso
        ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
        : '';
    setIssued(f(certificate.issued));
    setExpiry(certificate.expiry ? f(certificate.expiry) : null);
  }, [certificate.issued, certificate.expiry]);

  const issuerName = tenant?.issuerName || tenant?.name || 'Sigalmark';
  const status = STATUS_MAP[certificate.status] ?? 'VALID';

  const data: VerificationData = {
    issuer: {
      name: issuerName,
      initials: initialsOf(issuerName),
      logoUrl: resolveLogoSrc(tenant?.logoUrl),
      brandTint: withFallback(tenant?.tintElements, '#3F4A22'),
    },
    holderName: certificate.name,
    competency: certificate.competency,
    issuedAtLabel: issued,
    expiresAtLabel: expiry,
    statusPhrase: statusPhrase(status, expiry, certificate.expiry),
    status,
    artworkUrl: certificate.imageURI || undefined,
    // The QR is drawn client-side; there is no server-rendered image to link to.
    qrNode: (
      <StyledQRCode
        url={qrUrl}
        size={208}
        styleKey="swm"
        backgroundOptions={{
          marginTexts: generateMarginTexts(
            certificate.contract,
            certificate.tokenId,
            certificate.networkId,
          ),
          color: 'transparent',
        }}
        embedLogoUrl={resolveLogoSrc(tenant?.logoUrl)}
        tintElements={tenant?.tintElements ?? undefined}
        tintText={tenant?.tintText ?? undefined}
        tintBackground={tenant?.tintBackground ?? undefined}
      />
    ),
    chain: {
      contract: certificate.contract,
      tokenId: String(certificate.tokenId),
      chainId: getNetworkNameById(certificate.networkId) || String(certificate.networkId),
      explorerUrl: certificate.explorerUrl ?? '',
    },
  };

  void verificationCode;
  return <VerificationPage data={data} />;
}
